import { GuardSpinner } from '@/components/GuardSpinner';
import type { OnboardingGuardProps } from '@/components/OnboardingGuard.types';
import { hasSessionsWriteAccess, useAuth } from '@/contexts/AuthContext';
import { onboardingStatusQueryOptions } from '@/services/onboarding/useOnboardingStatus';
import { statusAccountId, useStatusGate } from '@/services/statusGate/useStatusGate';

/** Sends users with pending first-run onboarding to /onboarding (runs before PiiTrainingGuard). */
export const OnboardingGuard = ({ children }: OnboardingGuardProps) => {
  const { user, isLoading: isAuthLoading } = useAuth();
  const isBlocking = useStatusGate({
    queryOptions: onboardingStatusQueryOptions(statusAccountId(user)),
    // An admin impersonating a user must never be pushed into that user's onboarding, and a user
    // who cannot complete it (no sessions:write) must not be trapped there: fail open.
    enabled: !isAuthLoading && !!user && !user.impersonating && hasSessionsWriteAccess(user),
    redirectTo: '/onboarding',
    errorMessage: 'Failed to check onboarding status',
  });

  // Spinner while checking, and while redirecting so children never flash.
  if (isAuthLoading || isBlocking) return <GuardSpinner />;

  return <>{children}</>;
};
